import { useMemo, useRef } from 'react'
import { localizarComDestaque, useEquipamentosNaCena } from './contextoEquipamentos'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { orientacao, RAIO_ALVO_TOQUE_M, RAIO_CORDA_M, RAIO_SELECIONADA_M, SegmentoCorda, SetaCone } from './Cordas3D'
import { AnelDeFita } from '../cordas/AnelDeFita'
import { MosquetaoTabletop } from './ObjetosCordasTabletop'
import {
  ancoragensDeFitas,
  apoioDaFita,
  calcularAncoragem,
  comprimentoDoFioSemCarga,
  ladoDaPerna,
  lugarNaBarra,
  naBarra,
  pernasNaEscada,
  pontaDaFitaNaPeca,
  pecaSobCarga,
  definicaoDaFuncao,
  CONFIGURACAO_AVISOS_PADRAO,
  localizarPontoNoMundo,
  type AncoragemTabletop,
  type CenaTabletop,
  type CordaTabletop,
  type ConfiguracaoAvisos,
  type ObjetoTabletop,
  type ReferenciaPonto,
  type TipoAncoragem,
  type VetorTabletop,
} from '../../dominio/tabletop'

/**
 * As ancoragens em V, W e M na cena 3D: cada perna é um anel de fita, que
 * abraça a estrutura no ponto de fixação (na escada, o banzo) e termina no
 * ponto central. Com peso no ponto central (uma corda esticada passando ou
 * presa nele) as fitas esticam e ganham a seta; sem peso, pendem. A cor da perna diz o estado — dentro do limite, acima do limite ou
 * quase oposta — e o rótulo no centro escreve o ângulo e a carga por perna
 * (a cor nunca vai sozinha).
 */

export const COR_PERNA_OK = '#a3e635'
export const COR_PERNA_ACIMA_DO_LIMITE = '#f59e0b'
export const COR_PERNA_CRITICA = '#ef4444'

const inteiro = (valor: number) => String(Math.round(valor))

/** Cor e texto do rótulo de uma ancoragem, a partir do cálculo e do limite configurado. */
export function aparenciaDaAncoragem(
  cena: CenaTabletop,
  ancoragem: AncoragemTabletop,
  configuracao: ConfiguracaoAvisos,
): { cor: string; texto: string; subtexto?: string } {
  const r = calcularAncoragem(cena, ancoragem)
  if (r.incompleta) return { cor: COR_PERNA_CRITICA, texto: 'incompleta' }
  if (r.degenerada) return { cor: COR_PERNA_CRITICA, texto: `${inteiro(r.anguloMaximoGraus!)}°`, subtexto: 'pernas opostas' }
  const acima = r.anguloMaximoGraus! > configuracao.anguloMaximoAncoragemGraus
  return {
    cor: acima ? COR_PERNA_ACIMA_DO_LIMITE : COR_PERNA_OK,
    texto: `${inteiro(r.anguloMaximoGraus!)}°${acima ? ' acima do limite' : ''}`,
    subtexto:
      r.equalizacao === 'pre-equalizada'
        ? `pré-equalizada · ${r.fracoesPorPerna!.map((f) => `${inteiro(f * 100)}%`).join(' / ')}`
        : `${inteiro(r.fracaoPorPerna! * 100)}% por perna`,
  }
}

/** Peças que já desenham o ponto central de uma ancoragem. */
const TIPOS_COM_PECA_NO_CENTRO: ReadonlySet<string> = new Set(['mosquetao', 'polia', 'poliaDupla', 'placaAncoragem', 'bloqueador', 'descensor', 'noOito', 'noFiel'])

function AncoragemNaCena({
  cena,
  ancoragem,
  selecionada,
  interativa,
  configuracao,
  mostrarSetas,
  mostrarRotulo,
  aoSelecionar,
}: {
  cena: CenaTabletop
  ancoragem: AncoragemTabletop
  selecionada: boolean
  interativa: boolean
  configuracao: ConfiguracaoAvisos
  mostrarSetas: boolean
  mostrarRotulo: boolean
  aoSelecionar: (id: string) => void
}) {
  const inicioToque = useRef<{ x: number; y: number } | null>(null)
  // no modo de destaque, as pernas chegam aos pontos das peças aumentadas
  const { fatorDaPeca } = useEquipamentosNaCena()
  const centro = localizarComDestaque(cena, ancoragem.centro, fatorDaPeca)
  const { cor, texto, subtexto } = aparenciaDaAncoragem(cena, ancoragem, configuracao)
  // cada perna sai de onde o anel abraça a estrutura: na escada, o banzo, nunca o meio do degrau
  // na escada, as pernas ficam na mesma altura, uma em cada banzo
  const naEscada = pernasNaEscada(cena, ancoragem.fixacoes)
  const apoios = ancoragem.fixacoes.map((f, i) =>
    centro ? apoioDaFita(cena, f, centro, ladoDaPerna(i), fatorDaPeca(f.idObjeto), naEscada[i]) : null,
  )
  const fixacoes = apoios.map((a) => a?.ponto ?? null)
  const carregada = pecaSobCarga(cena, ancoragem.centro.idObjeto)
  if (!centro) return null
  // no olhal de uma polia (placa, sistema...) as fitas clipam num mosquetão, e ele no olhal
  // num mosquetão, as fitas abraçam a ponta dele voltada para elas
  const fim = pontaDaFitaNaPeca(
    cena,
    ancoragem.centro,
    fixacoes.filter((f): f is VetorTabletop => f !== null),
    fatorDaPeca(ancoragem.centro.idObjeto),
  )
  const mosquetao = fim?.mosquetao ?? null
  const fimDasFitas = fim?.ponta ?? centro
  // cada fita abraça a barra do mosquetão no seu lugar, lado a lado com as outras
  const fBarra = fatorDaPeca(ancoragem.centro.idObjeto)
  const barras = fixacoes.map((_, i) => (fim?.barra ? naBarra(fim.barra, lugarNaBarra(i, fixacoes.length, fBarra)) : null))

  return (
    <group name={`ancoragem-${ancoragem.id}`}>
      {fixacoes.map((fixacao, i) => {
        if (!fixacao) return null
        const { comprimento, quaternion } = orientacao(fixacao, centro)
        return (
          <group key={i}>
            {selecionada && <SegmentoCorda de={fixacao} para={centro} cor="#ffffff" raio={RAIO_SELECIONADA_M} opacidade={0.5} />}
            {/* cada perna é um anel de fita, do apoio ao ponto central; fora do limite, ganha a cor do alerta */}
            <AnelDeFita
              apoio={fixacao}
              para={barras[i]?.centro ?? fimDasFitas}
              barra={barras[i] ?? null}
              eixo={apoios[i]!.eixo}
              raioDoApoio={apoios[i]!.raio}
              carregada={carregada}
              comprimentoSemCargaM={comprimentoDoFioSemCarga(
                Math.hypot(fimDasFitas[0] - fixacao[0], fimDasFitas[1] - fixacao[1], fimDasFitas[2] - fixacao[2]),
                apoios[i]!.raio,
              )}
              cor={cor === COR_PERNA_OK ? definicaoDaFuncao('fita').cor : cor}
            />
            {/* a perna puxa a carga para a fixação: a seta fica perto do ponto central e aponta para ela; sem peso, não puxa */}
            {mostrarSetas && carregada && comprimento > 0.6 && (
              <SetaCone
                posicao={[
                  centro[0] + ((fixacao[0] - centro[0]) / comprimento) * 0.6,
                  centro[1] + ((fixacao[1] - centro[1]) / comprimento) * 0.6,
                  centro[2] + ((fixacao[2] - centro[2]) / comprimento) * 0.6,
                ]}
                direcao={[(fixacao[0] - centro[0]) / comprimento, (fixacao[1] - centro[1]) / comprimento, (fixacao[2] - centro[2]) / comprimento]}
                cor={cor}
              />
            )}
            {interativa && comprimento > 1e-6 && (
              <mesh
                position={[(fixacao[0] + centro[0]) / 2, (fixacao[1] + centro[1]) / 2, (fixacao[2] + centro[2]) / 2]}
                quaternion={quaternion}
                onPointerDown={(evento) => {
                  evento.stopPropagation()
                  inicioToque.current = { x: evento.nativeEvent.clientX, y: evento.nativeEvent.clientY }
                }}
                onClick={(evento) => {
                  evento.stopPropagation()
                  const inicio = inicioToque.current
                  inicioToque.current = null
                  if (inicio && Math.hypot(evento.nativeEvent.clientX - inicio.x, evento.nativeEvent.clientY - inicio.y) > 6) return
                  aoSelecionar(ancoragem.id)
                }}
              >
                <cylinderGeometry args={[RAIO_ALVO_TOQUE_M, RAIO_ALVO_TOQUE_M, comprimento, 6]} />
                <meshBasicMaterial transparent opacity={0} depthWrite={false} />
              </mesh>
            )}
          </group>
        )
      })}
      {mosquetao && (
        <group name="mosquetao-da-fita" position={[mosquetao.centro[0], mosquetao.centro[1], mosquetao.centro[2]]} scale={fatorDaPeca(ancoragem.centro.idObjeto)}>
          <MosquetaoTabletop semAlvo eixo={mosquetao.eixo} />
        </group>
      )}
      {/* sem peça no ponto central (mosquetão, polia, placa), uma bolinha marca onde as pernas se juntam */}
      {!TIPOS_COM_PECA_NO_CENTRO.has(cena.objetos.find((o) => o.id === ancoragem.centro.idObjeto)?.tipo ?? 'cone') && (
        <mesh raycast={() => null} position={[centro[0], centro[1], centro[2]]}>
          <sphereGeometry args={[RAIO_CORDA_M * 2.2, 12, 10]} />
          <meshStandardMaterial color={cor} roughness={0.5} />
        </mesh>
      )}
      {mostrarRotulo && (
        <Rotulo3D
          posicao={[centro[0], centro[1] + 0.22, centro[2]]}
          texto={`${ancoragem.tipo} · ${texto}`}
          subtexto={subtexto}
          variante={cor === COR_PERNA_OK ? 'neutro' : 'risco'}
          sempreVisivel
          tamanhoFixo
        />
      )}
    </group>
  )
}

/**
 * O rótulo de uma ancoragem reconhecida das fitas (duas fitas no mesmo
 * mosquetão são um V): o ângulo e a carga por perna, sobre o ponto central.
 */
function RotuloDaAncoragemDeFitas({
  cena,
  ancoragem,
  configuracao,
}: {
  cena: CenaTabletop
  ancoragem: AncoragemTabletop
  configuracao: ConfiguracaoAvisos
}) {
  const { fatorDaPeca } = useEquipamentosNaCena()
  const centro = localizarComDestaque(cena, ancoragem.centro, fatorDaPeca)
  if (!centro) return null
  const { cor, texto, subtexto } = aparenciaDaAncoragem(cena, ancoragem, configuracao)
  return (
    <group name={`ancoragem-${ancoragem.id}`}>
      <Rotulo3D
        posicao={[centro[0], centro[1] + 0.22, centro[2]]}
        texto={`${ancoragem.tipo} de fitas · ${texto}`}
        subtexto={subtexto}
        variante={cor === COR_PERNA_OK ? 'neutro' : 'risco'}
        sempreVisivel
        tamanhoFixo
      />
    </group>
  )
}

/** Os pontos já escolhidos da ancoragem em desenho: uma bolinha branca em cada. */
function DesenhoDaAncoragem({
  cena,
  tipo,
  fixacoes,
  centro,
}: {
  cena: CenaTabletop
  tipo: TipoAncoragem
  fixacoes: readonly ReferenciaPonto[]
  centro: ReferenciaPonto | null
}) {
  const pontos: VetorTabletop[] = [...fixacoes, ...(centro ? [centro] : [])]
    .map((r) => localizarPontoNoMundo(cena, r))
    .filter((p): p is VetorTabletop => p !== null)
  return (
    <group name={`ancoragem-em-desenho-${tipo}`}>
      {pontos.map((p, i) => (
        <mesh key={i} raycast={() => null} position={[p[0], p[1], p[2]]} renderOrder={11}>
          <sphereGeometry args={[0.06, 14, 10]} />
          <meshBasicMaterial color="#ffffff" depthTest={false} transparent opacity={0.9} />
        </mesh>
      ))}
    </group>
  )
}

export function Ancoragens3D({
  objetos,
  cordas,
  ancoragens,
  idSelecionada,
  aoSelecionar,
  desenho,
  configuracao = CONFIGURACAO_AVISOS_PADRAO,
  mostrarSetas = true,
  ocultarRotulos = false,
  pontoComAcoes = null,
}: {
  objetos: readonly ObjetoTabletop[]
  /** As cordas da cena: é por elas que se sabe se o ponto central tem peso. */
  cordas?: readonly CordaTabletop[]
  ancoragens: readonly AncoragemTabletop[]
  idSelecionada: string | null
  /** Sem isto as ancoragens só se veem; não se tocam. */
  aoSelecionar?: (id: string) => void
  desenho?: { tipo: TipoAncoragem; fixacoes: readonly ReferenciaPonto[]; centro: ReferenciaPonto | null } | null
  configuracao?: ConfiguracaoAvisos
  /** Setas de tração: cada perna aponta para a fixação que a segura (padrão ligado). */
  mostrarSetas?: boolean
  /** Montando (peça na mão, corda em desenho): os rótulos saem da frente dos pontos. */
  ocultarRotulos?: boolean
  /** O ponto com as ações abertas: a ancoragem centrada nele mostra o resumo na barra, não sobre a cena. */
  pontoComAcoes?: ReferenciaPonto | null
}) {
  const centradaNoPonto = (a: AncoragemTabletop) =>
    pontoComAcoes !== null && a.centro.idObjeto === pontoComAcoes.idObjeto && a.centro.idPonto === pontoComAcoes.idPonto
  const cena = useMemo<CenaTabletop>(() => ({ versao: 2, nome: '', objetos, ancoragens, cordas }), [objetos, ancoragens, cordas])
  const deFitas = useMemo(() => ancoragensDeFitas(cena), [cena])
  return (
    <group name="ancoragens">
      {ancoragens.map((a) => (
        <AncoragemNaCena
          key={a.id}
          cena={cena}
          ancoragem={a}
          selecionada={a.id === idSelecionada}
          // enquanto se desenha, a perna existente não rouba o toque dos pontos
          interativa={Boolean(aoSelecionar) && !desenho}
          configuracao={configuracao}
          mostrarSetas={mostrarSetas}
          mostrarRotulo={!ocultarRotulos && !centradaNoPonto(a)}
          aoSelecionar={aoSelecionar ?? (() => {})}
        />
      ))}
      {/* as reconhecidas das fitas: as fitas já desenham as pernas; aqui só o ângulo e a carga */}
      {!ocultarRotulos &&
        deFitas
          .filter((a) => !centradaNoPonto(a))
          .map((a) => <RotuloDaAncoragemDeFitas key={a.id} cena={cena} ancoragem={a} configuracao={configuracao} />)}
      {desenho && <DesenhoDaAncoragem cena={cena} tipo={desenho.tipo} fixacoes={desenho.fixacoes} centro={desenho.centro} />}
    </group>
  )
}
